import React, { useState, useEffect } from 'react'
import { ReviewListType } from '../constants/reviewListType'
import { useLocation, useNavigate } from 'react-router-dom'
import AppBar from '../components/AppBar'
import { clearAuthHistory } from '../utils/platform'
import BottomNav from '../components/BottomNav'
import { BOTTOM_NAV_HEIGHT } from '../constants/layout'
import { MY_NAV_ITEM } from '../types/navItem'
import { userApi, UserProfile, ReviewCategory } from '../api/user'
import { bookmarkApi } from '../api/bookmark'
import Gap from '../components/Gap'
import OtherUserProfileInfo from '../components/OtherUserProfileInfo'
import UserStatisticInfo from '../components/UserStatisticInfo'
import DefaultFolderGrid from '../components/DefaultFolderGrid'
import MediumBadgeButton from '../components/MediumBadgeButton'
import PlateList from '../components/PlateList'

// 마이 탭 캐시: 하위 화면(플레이트리스트 등)에 다녀와도 로딩 화면 없이
// 즉시 표시하고 백그라운드에서 최신 데이터로 갱신 (SWR 패턴).
// 계정이 바뀌면(토큰 변경) 캐시 무효화.
let cachedUser: UserProfile | null = null
let cachedCategories: ReviewCategory[] = []
let cachedAuthToken: string | null = null

/**
 * 목록이 실질적으로 같은가.
 *
 * 화면에 그리는 값(이름·썸네일·공개 여부)만 본다. 서버가 순서를 바꿔 주거나
 * 안 쓰는 필드가 달라졌다고 다시 그릴 이유는 없다.
 */
const isSameCategoryList = (a: ReviewCategory[], b: ReviewCategory[]): boolean => {
  if (a.length !== b.length) return false

  return a.every((x, i) => {
    const y = b[i]
    return (
      x.id === y.id &&
      x.name === y.name &&
      x.thumbnail_image === y.thumbnail_image &&
      x.is_public === y.is_public
    )
  })
}

const MainMy: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  // 학교 그룹처럼 다른 화면에서 '내 프로필'로 들어온 경우에만 true
  const showBack = (location.state as { showBack?: boolean } | null)?.showBack === true

  // 계정이 바뀌었으면 이전 계정의 캐시 폐기
  const currentToken = localStorage.getItem('auth_token')
  if (cachedAuthToken !== currentToken) {
    cachedUser = null
    cachedCategories = []
    cachedAuthToken = currentToken
  }

  // 메인 페이지 진입 시 이전 히스토리 정리
  useEffect(() => {
    clearAuthHistory()
  }, [])

  // 프로필 정보 및 리뷰 카테고리 목록 로드
  useEffect(() => {
    loadProfile()
    loadReviewCategories()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [user, setUser] = useState<UserProfile | null>(cachedUser)
  const [categories, setCategories] =
    useState<ReviewCategory[]>(cachedCategories)
  const [isLoadingProfile, setIsLoadingProfile] = useState(false)

  const loadProfile = async () => {
    // 캐시가 있으면 로딩 화면 없이 백그라운드 갱신만
    if (!cachedUser) {
      setIsLoadingProfile(true)
    }
    try {
      const response = await userApi.getProfile()
      cachedUser = response.user
      setUser(response.user)
    } catch (error) {
      console.error('프로필 정보 로드 실패:', error)
      // 에러는 apiClient의 interceptor에서 이미 처리됨
    } finally {
      setIsLoadingProfile(false)
    }
  }

  const loadReviewCategories = async () => {
    try {
      const response = await bookmarkApi.getBookmarkCategories()
      cachedCategories = response.categories

      /*
       * 내용이 그대로면 상태를 건드리지 않는다.
       *
       * 플레이트리스트에 들어갔다 스와이프로 돌아올 때마다 이 화면이 다시
       * 붙으면서 목록을 새로 받는다. 받은 값이 같아도 새 배열이라 React는
       * 전부 다른 항목으로 보고 그리드를 통째로 다시 그렸다. 썸네일이
       * 이미지라 그때마다 깜빡였다.
       *
       * 캐시가 이미 화면에 그려져 있으므로, 실제로 바뀐 게 있을 때만 바꾼다.
       */
      setCategories((prev) =>
        isSameCategoryList(prev, response.categories) ? prev : response.categories
      )
    } catch (error) {
      console.error('북마크 카테고리 목록 로드 실패:', error)
      // 에러는 apiClient의 interceptor에서 이미 처리됨
    }
  }

  const handleProfileEditClick = () => {
    navigate('/profile-edit')
  }

  const handleSettingsClick = () => {
    navigate('/settings')
  }

  const handleCategoryClick = (categoryId: string, name?: string) => {
    if (!user) return
    // 이름을 state로 함께 넘겨 로딩 중에도 올바른 제목이 보이게 한다(빈 제목 깜빡임 방지)
    navigate(`/user/${user.id}/plate-category/${categoryId}`, {
      state: { categoryName: name },
    })
  }

  if (isLoadingProfile || !user) {
    return (
      <div className="w-full h-full flex flex-col bg-bgPrimary">
        <div className="app-container flex-1 flex flex-col overflow-y-auto scrollbar-hide">
          <div className="w-full flex items-center justify-center py-48px">
            <div className="text-style-captionL text-textColorText3">
              프로필 정보를 불러오는 중...
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderButtons = () => {
    return (
      <div className="w-full flex items-center justify-center gap-10px">
        <MediumBadgeButton
          label="프로필 수정"
          onClick={handleProfileEditClick}
          variant="inverse"
          className="flex-1"
        />
        <MediumBadgeButton
          label="환경설정"
          onClick={handleSettingsClick}
          variant="inverse"
          className="flex-1"
        />
      </div>
    )
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      {/*
        다른 화면(학교 그룹 등)에서 '내 프로필'로 들어왔을 때만 뒤로가기를 단다.
        탭으로 들어온 마이는 첫 화면이라 돌아갈 곳이 없으니 그대로 둔다.
      */}
      {showBack && (
        // 다른 사람 프로필 화면(OtherUserProfile)과 같은 머리: 뒤로가기 옆에 아이디
        <AppBar
          title={user.username}
          titleAlign="left"
          titleFontSize={20}
          onBack={() => navigate(-1)}
        />
      )}
      <div className="flex-1 flex flex-col overflow-y-auto scrollbar-hide">
        <div className="app-container w-full px-20px">
          <Gap height={16} />

          {/* 아이디 — 뒤로가기 머리에 이미 있으면 다시 쓰지 않는다 */}
          {!showBack && (
            <>
              <p className="font-pretendard font-bold text-20px leading-180% text-textColorText2">
                {user.username}
              </p>

              <Gap height={16} />
            </>
          )}

          {/* 사용자 정보 */}
          <OtherUserProfileInfo
            nickname={user.nickname}
            profileImage={user.profile_image || undefined}
            username={user.username}
            bio={user.bio}
            consecutiveAttendanceDays={user.consecutive_attendance_days}
            tasteTrait={user.taste_trait}
            emphasizeNicknameInProfileRow
          />

          <Gap height={16} />

          {/* 통계 정보 */}
          <UserStatisticInfo
            reviewCount={user.review_count}
            followerCount={user.follower_count}
            followingCount={user.following_count}
            onReviewClick={() =>
              navigate(
                `/reviews?userId=${user.id}&type=${ReviewListType.REVIEWS}`
              )
            }
            onFollowerClick={() => navigate('/follow-list/followers')}
            onFollowingClick={() => navigate('/follow-list/following')}
          />

          <Gap height={16} />

          {/* 버튼들 */}
          {renderButtons()}
        </div>

        <Gap height={16} />

        <div className="app-container w-full px-20px">
          {/* 기본 폴더 */}
          <DefaultFolderGrid userId={user.id} />
        </div>

        <div className="app-container w-full px-20px">
          {/* divider */}
          <div className="w-full h-1px bg-brandPrimary-10% shrink-0"></div>
        </div>

        <Gap height={16} />

        <div className="app-container w-full">
          {/* 플레이트리스트 */}
          <PlateList
            categories={categories}
            onCategoryClick={handleCategoryClick}
            isMine={true}
            onCategoryAdded={loadReviewCategories}
          />

          <Gap height={16} />
        </div>
      </div>

      {/* 바텀 네비게이션 */}
      <div
        className="w-full shrink-0"
        style={{ height: `${BOTTOM_NAV_HEIGHT}px` }}
      ></div>
      <BottomNav activePath={MY_NAV_ITEM.path} />
    </div>
  )
}

export default MainMy
